import React, { useCallback, useEffect, useState, useMemo } from "react";
import SearchBox from "../../common/SearchBox";
import { userActivityData } from "../../../services/userActivityService";
import { FaDownload } from "react-icons/fa";
import ContextMenu from "../../common/ContextMenu";
import { useToast } from "../../common/ToastContext";
import AutoRefreshTimer from "../../common/AutoRefreshTimer";
import UserCard from "./UserCard";
import ToggleControl from "../../common/ToggleControl";
import MetricsHeader from "./MetricsHeader";

const UserMonitor = () => {
  const [tableData, setTableData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [contextMenu, setContextMenu] = useState(null);
  const [activeFilter, setActiveFilter] = useState("");
  const [isRegexMode, setIsRegexMode] = useState(() => {
    return localStorage.getItem("search-regex-mode") === "true";
  });
  const [compactMode, setCompactMode] = useState(true);
  const { showToast } = useToast();

  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    activeUsers: 0,
    averageSessionTime: "0h",
    mostCommonRole: "",
    longestActiveUser: "",
    newUsers: 0,
    roleDistribution: {},
  });

  const getData = async () => {
    try {
      const data = await userActivityData();
      setTableData(Object.values(data));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const clearActiveFilter = () => {
    setActiveFilter("");
  };

  const filterOptions = useMemo(() => {
    const options = [
      { label: "All Users", value: "", isRegexMode: false },
      {
        label: "Active Today",
        value: "active-today",
        isRegexMode: false,
        onSelect: () => setActiveFilter("active-today"),
      },
      {
        label: "Current Users",
        value: "current-users",
        isRegexMode: false,
        onSelect: () => setActiveFilter("current-users"),
      },
      {
        label: "Recent Users",
        value: "recent-users",
        isRegexMode: false,
        onSelect: () => setActiveFilter("recent-users"),
      },
      {
        label: "Inactive Users",
        value: "inactive-users",
        isRegexMode: false,
        onSelect: () => setActiveFilter("inactive-users"),
      },
      {
        label: "Admins",
        value: "admin",
        isRegexMode: false,
        onSelect: () => setActiveFilter("admin"),
      },
      {
        label: "Prompt Engineers",
        value: "prompt-engineer",
        isRegexMode: false,
        onSelect: () => setActiveFilter("prompt-engineer"),
      },
      {
        label: "Clinical Authors",
        value: "clinical-author",
        isRegexMode: false,
        onSelect: () => setActiveFilter("clinical-author"),
      },
      {
        label: "Demonstrators",
        value: "demonstrator",
        isRegexMode: false,
        onSelect: () => setActiveFilter("demonstrator"),
      },
      {
        label: "Viewers",
        value: "viewer",
        isRegexMode: false,
        onSelect: () => setActiveFilter("viewer"),
      },
      {
        label: "Champions",
        value: "champion",
        isRegexMode: false,
        onSelect: () => setActiveFilter("champion"),
      },
    ];
    return options;
  }, []);

  useEffect(() => {
    getData();
  }, []);

  const handleRefresh = () => {
    getData();
    setTimeout(() => showToast("Refresh complete", "success"), 500); // prevent a conflict with the auto refresh control
  };

  const handleSearch = useCallback((term, regexMode) => {
    setSearchTerm(term);
    setIsRegexMode(regexMode);
  }, []);

  const filteredData = React.useMemo(() => {
    const now = new Date();
    const getMinutes = (user) => {
      const lastActive = new Date(user.lastActive);
      const diffMinutes = (now - lastActive) / (1000 * 60);
      return diffMinutes;
    };
    const filterByRole = (user, role) => {
      return user.lastRoles.includes(role);
    };

    let filtered = tableData.filter((user) => {
      const searchableFields = ["userName", "lastRoles", "recents"];
      const matchesSearch = searchableFields.some((field) => {
        const value = user[field];
        const stringValue =
          value !== null && value !== undefined ? String(value) : "";
        if (isRegexMode) {
          try {
            const regex = new RegExp(searchTerm, "i");
            return regex.test(stringValue);
          } catch (e) {
            return stringValue.toLowerCase().includes(searchTerm.toLowerCase());
          }
        } else {
          return stringValue
            .toLowerCase()
            .includes((searchTerm ?? "").toLowerCase());
        }
      });

      const mins = getMinutes(user);
      const minsSinceMidnightLocally = now.getHours() * 60 + now.getMinutes();
      switch (activeFilter.toLowerCase()) {
        case "active-today":
          return mins <= minsSinceMidnightLocally;
        case "current-users":
          return mins <= 5;
        case "recent-users":
          return mins > 5 && mins <= 30;
        case "inactive-users":
          return mins > 30;
        case "admin":
        case "prompt-engineer":
        case "clinical-author":
        case "demonstrator":
        case "viewer":
        case "champion":
          return filterByRole(user, activeFilter);
        default:
          return matchesSearch;
      }
    });

    filtered.sort((a, b) => new Date(b.lastActive) - new Date(a.lastActive));
    return filtered;
  }, [tableData, searchTerm, isRegexMode, activeFilter]);

  useEffect(() => {
    const calculateMetrics = () => {
      const totalUsers = filteredData.length;

      const activeNow = filteredData.filter((user) => {
        const lastActive = new Date(user.lastActive);
        return new Date() - lastActive <= 5 * 60 * 1000; // active in last 5 minutes
      }).length;

      const activeRecently =
        filteredData.filter((user) => {
          const lastActive = new Date(user.lastActive);
          return new Date() - lastActive <= 30 * 60 * 1000; // active in last 30 minutes
        }).length - activeNow;

      const roleCounts = filteredData.reduce((acc, user) => {
        user.lastRoles.forEach((role) => {
          acc[role] = (acc[role] || 0) + 1;
        });
        return acc;
      }, {});

      const newUsers = filteredData.filter((user) => {
        const firstActive = new Date(user.firstActive);
        return new Date() - firstActive <= 24 * 60 * 60 * 1000; // joined in last 24 hours
      }).length;

      setMetrics({
        totalUsers,
        activeNow,
        activeRecently,
        timeDistribution: filteredData.map((user) => user.timeSpent),
        roleDistribution: roleCounts,
        newUsers,
        joinDates: filteredData.map((user) => user.firstActive),
      });
    };

    calculateMetrics();
  }, [filteredData]);

  const handleContextMenu = (event) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const downloadCSV = () => {
    const headers = [
      "User Name",
      "First Active",
      "Last Active",
      "Time Active",
      "Latest Roles",
      "Recent Routes",
    ];
    const csvContent = [
      headers.join(","),
      ...filteredData.map((user) =>
        [
          user.userName,
          new Date(user.firstActive).toLocaleString().replaceAll(",", " "),
          new Date(user.lastActive).toLocaleString().replaceAll(",", " "),
          formatElapsed(user.timeSpent),
          user.lastRoles.join(";"),
          user.recents.join(";"),
        ].join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "user_activity_dashboard.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const contextMenuItems = [
    {
      label: "Download as CSV",
      icon: FaDownload,
      onClick: downloadCSV,
    },
  ];

  const getActivityStatusColor = (lastActiveDate) => {
    const now = new Date();
    const lastActive = new Date(lastActiveDate);
    const diffMinutes = (now - lastActive) / (1000 * 60);
    if (diffMinutes <= 5) return "bg-green-500";
    if (diffMinutes <= 30) return "bg-yellow-500";
    return "bg-gray-600";
  };

  const formatDuration = (date, suffix = "ago", compact = false) => {
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes < 2) return "now";
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    if (years > 0)
      return `${years} ${compact ? "yr" : "year"}${
        years > 1 ? "s" : ""
      } ${suffix}`;
    if (months > 0)
      return `${months} ${compact ? "mo" : "month"}${
        months > 1 ? "s" : ""
      } ${suffix}`;
    if (weeks > 0)
      return `${weeks} ${compact ? "wk" : "week"}${
        weeks > 1 ? "s" : ""
      } ${suffix}`;
    if (days > 0)
      return `${days} ${compact ? "day" : "day"}${
        days > 1 ? "s" : ""
      } ${suffix}`;
    if (hours > 0)
      return `${hours} ${compact ? "hr" : "hour"}${
        hours > 1 ? "s" : ""
      } ${suffix}`;
    return `${minutes} ${compact ? "min" : "minute"}${
      minutes > 1 ? "s" : ""
    } ${suffix}`;
  };

  const formatElapsed = (seconds, compact = false) => {
    const hours = seconds / 3600;
    return hours.toFixed(0) + (compact ? " hrs" : " hours");
  };

  const toggleCompactMode = (checked) => {
    setCompactMode(checked);
  };

  const gridColumnClass = compactMode
    ? "sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
    : "sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-2 2xl:grid-cols-4";

  return (
    <div
      className="h-[calc(100vh-4rem)] flex text-gray-100 p-4"
      onContextMenu={handleContextMenu}
    >
      <div className="hidden md:block h-full">
        <MetricsHeader data={metrics} />
      </div>
      <div className="flex-grow flex flex-col">
        <div className="absolute top-14 lg:top-4 left-[50%] translate-x-[-50%] w-[90%] max-w-[450px]">
          <SearchBox
            value={searchTerm}
            onSearch={handleSearch}
            placeholder="Filter Users ..."
            className="w-full max-w-md mx-auto"
            showRefresh={true}
            onRefresh={handleRefresh}
            filterOptions={filterOptions}
            activeFilter={
              activeFilter
                ? filterOptions.find((option) => option.value === activeFilter)
                : null
            }
            onFilterClear={clearActiveFilter}
            isRegexMode={isRegexMode}
          />
        </div>
        <div className="absolute top-5 left-3/4 -translate-x-3/4">
          <AutoRefreshTimer
            offLabel="Auto Refresh"
            onLabel=""
            initialState={true}
            onRefresh={handleRefresh}
          />
        </div>
        <div className="absolute top-5 left-1/4 -translate-x-1/4 w-[112.5px]">
          <ToggleControl
            offLabel="Detailed"
            onLabel="Compact"
            offIcon=""
            onIcon=""
            initialState={true}
            onChange={toggleCompactMode}
          />
        </div>
        <div className="flex-grow overflow-auto mt-8 lg:mt-0 dark-slim-scrollbar">
          <div className={`grid ${gridColumnClass} gap-4`}>
            {filteredData.map((user, index) => (
              <UserCard
                key={index}
                user={user}
                getActivityStatusColor={getActivityStatusColor}
                formatDuration={formatDuration}
                formatElapsed={formatElapsed}
                compact={compactMode}
              />
            ))}
          </div>
        </div>
      </div>
      {contextMenu && (
        <ContextMenu
          items={contextMenuItems}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={closeContextMenu}
        />
      )}
    </div>
  );
};

export default UserMonitor;
